/**
 * Le plan d'édition atomique SuperDoc (#1610).
 *
 * `preview` et `apply` sont deux phases du MÊME plan compilé localement. Le
 * premier ne modifie jamais l'acte ; le second est l'unique mutation externe.
 * Il ne faut pas le remplacer par une boucle d'exécuteurs unitaires : chaque
 * attente entre deux appels rouvrirait une fenêtre où le document peut changer
 * et invalider les cibles suivantes.
 *
 * Depuis #1947, un plan qui porte une étape `format_apply` RELIT la forme au
 * fichier exporté après l'application : l'accusé de l'éditeur ne prouve pas
 * une forme. Ce que la relecture juge, ce qu'elle dit « non lisible » et ce
 * que le reçu en fait est dans l'en-tête de `formeDuPlan.ts`. Un plan sans
 * étape de forme n'exporte rien : son reçu est celui d'avant.
 *
 * Ce que cette relecture coûte : sonde `tests/ws-mocked/mesure-plan-forme-relue-cout.spec.ts`
 * (SuperDoc 2.15.1, 2026-10-05), qui joue les deux bras du même plan de 40
 * étapes de forme sur l'acte de banc augmenté de 40 paragraphes, trois passes
 * chacun, sonde jouée deux fois. De l'enveloppe au reçu : 854 à 858 ms avec la
 * relecture, 855 à 860 ms sans (export rendu indisponible par une doublure, la
 * relecture renonce aussitôt) ; l'unique export en mémoire du plan y prend 2 à
 * 6 ms.
 * L'écart n'est pas mesurable à ce grain sur un acte de cette taille. ⚠️
 * L'export grandit avec l'acte (en-tête de `exportDeLActe.ts`), et la
 * relecture d'un gros acte n'a pas été mesurée : il reste UN export par plan.
 */

import type { CanvasActionInput } from '@hector/shared-types';

import {
  abortedBeforeMutation,
  buildVerifiedReceipt,
  inconclusiveAfterMutation,
  partialAfterMutation,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
  type ResolvedActionTarget,
  type VerificationCheck,
} from './actionReceipt';
import { lirePreview, lireRecuDePlan, resolutionsParEtape, type ProviderPreview, type RecuDePlan, type ResolutionDEtape } from './apercuDePlan';
import { idsDeRevision } from './canvasSnapshot';
import { corpsRelu, relireLActeApresLEcriture } from './ecritureDeForme';
import { REFUS_TEXTE_DANS_UN_CHAMP } from './champsVides';
import { captureCanvasAgentTransportDiagnostic } from '../../diagnostics/canvasWriteDiagnostics';
import { estArretDeLot } from './executionAsynchrone';
import { EDIT_PLAN_OPERATION_BY_STEP, type NativeEditPlanOperation } from './editPlanContract';
import { resoudreLaCible } from './formatActionExecutors';
import { etapesDeFormeDuPlan, jugerLaFormeDuPlan, recuAvecLaFormeDuPlan } from './formeDuPlan';
import { lireLaRevisionDuDocument, muterSousFences, REVISION_ABSENTE, revisionCourante } from './fencesDeMutation';
import { estUnParagrapheDeCelluleAuFichier, messageParagrapheDeCelluleNonResolu, resoudreUnParagrapheDeCellule, type ParagrapheDeCelluleResolu } from './selectorResolver';
import { modeDeRevision, snapshot, targetSummary, type CanvasActionContext } from './textActionExecutors';

type ApplyEditPlanInput = Extract<CanvasActionInput, { action: 'apply_edit_plan' }>;
type PlanStep = ApplyEditPlanInput['steps'][number];
type ProviderStepOperation = NativeEditPlanOperation;
function operationProvider(step: PlanStep): ProviderStepOperation {
  return EDIT_PLAN_OPERATION_BY_STEP[step.op];
}

function addressOf(target: { nodeId: string; nodeType: string }) {
  return { kind: 'block' as const, nodeType: target.nodeType, nodeId: target.nodeId };
}

function textWhere(target: { nodeId: string; nodeType: string }, pattern: string) {
  return {
    by: 'select' as const,
    select: { type: 'text' as const, pattern, caseSensitive: true },
    within: addressOf(target),
    require: 'exactlyOne' as const,
  };
}

/** Compile model-safe selectors into the provider's current mutation-plan shape. */
function compilerEtape(step: PlanStep, target: { nodeId: string; nodeType: string }): Record<string, unknown> {
  switch (step.op) {
    case 'assert_text':
      return {
        id: step.id,
        op: 'assert',
        where: {
          by: 'select',
          select: { type: 'text', pattern: step.text, caseSensitive: true },
          within: addressOf(target),
        },
        args: { expectCount: step.expectedCount },
      };
    case 'text_rewrite':
      return {
        id: step.id,
        op: 'text.rewrite',
        where: textWhere(target, step.find),
        args: { replacement: { text: step.replace } },
      };
    case 'text_insert':
      return {
        id: step.id,
        op: 'text.insert',
        where: { by: 'block', ...addressOf(target) },
        args: { position: step.position, content: { text: step.text } },
      };
    case 'text_delete':
      return {
        id: step.id,
        op: 'text.delete',
        where: textWhere(target, step.text),
        args: {},
      };
    case 'format_apply':
      return {
        id: step.id,
        op: 'format.apply',
        where: textWhere(target, step.text),
        args: { inline: step.mark },
      };
  }
}

function previewCoversExactly(input: ApplyEditPlanInput, preview: ProviderPreview): boolean {
  return preview.steps.length === input.steps.length
    && preview.steps.every((step, index) => step.stepId === input.steps[index]?.id && step.op === operationProvider(input.steps[index]!));
}

/**
 * Les révisions déjà en attente dans lesquelles TOUT le plan s'est fondu, ou aucune.
 *
 * WHY : retoucher un paragraphe encore en insertion suivie ne crée aucune identité — l'éditeur range la retouche dans
 * la révision ouverte et la nomme dans `trackedChangeIds`. Le reçu disait alors `partial` / `TRACKING_NOT_APPLIED`
 * (« présent sans trace relisible ») d'un texte que l'avocat voit toujours en révision (conversation de production du
 * 2026-10-13). La preuve est stricte : CHAQUE étape qui écrit porte au moins une révision, et chacune est ouverte
 * avant et après l'écriture. Une seule étape sans révision, et le plan garde son reçu `partial`.
 */
function revisionsAbsorbantesDuPlan(
  input: ApplyEditPlanInput,
  receipt: RecuDePlan,
  revisionsAvant: readonly string[],
  revisionsApres: readonly string[],
): string[] {
  const ecrivantes = receipt.steps.filter((_, rang) => input.steps[rang]?.op !== 'assert_text');
  const ouverteAvantEtApres = (id: string): boolean => revisionsAvant.includes(id) && revisionsApres.includes(id);
  const toutesAbsorbees = ecrivantes.length > 0
    && ecrivantes.every((step) => step.trackedChangeIds !== undefined && step.trackedChangeIds.length > 0 && step.trackedChangeIds.every(ouverteAvantEtApres));
  return toutesAbsorbees ? [...new Set(ecrivantes.flatMap((step) => step.trackedChangeIds ?? []))] : [];
}

function proofFromReceipt(input: ApplyEditPlanInput, receipt: RecuDePlan) {
  return input.steps.map((step, index) => {
    const actual = receipt.steps[index];
    const sameStep = actual !== undefined && actual.stepId === step.id && actual.op === operationProvider(step);
    const effect = sameStep ? actual.effect : 'error';
    const matchCount = sameStep ? actual.matchCount : 0;
    const effectExpected = step.op === 'assert_text' ? 'assert_passed' : 'changed';
    const expectedCount = step.op === 'assert_text' ? step.expectedCount : undefined;
    const verified = sameStep
      && effect === effectExpected
      && (expectedCount === undefined ? matchCount > 0 : matchCount === expectedCount);
    return {
      id: step.id,
      op: step.op,
      effect,
      matchCount,
      ...(actual?.trackedChangeIds === undefined ? {} : { trackedChangeIds: actual.trackedChangeIds }),
      verified,
      ...(verified ? {} : { message: sameStep ? 'The native plan receipt did not prove this requested step.' : 'The native plan receipt did not return this stable step in order.' }),
    };
  });
}

/** Longueur maximale du texte cherché recopié dans un refus : assez pour le reconnaître, jamais un paragraphe. */
const LONGUEUR_MAX_DU_TEXTE_CHERCHE = 80;

/** Le texte qu'une étape cherche dans son bloc ; `text_insert` n'en cherche aucun. */
function texteCherche(step: PlanStep): string | undefined {
  switch (step.op) {
    case 'text_rewrite':
      return step.find;
    case 'assert_text':
    case 'text_delete':
    case 'format_apply':
      return step.text;
    case 'text_insert':
      return undefined;
  }
}

function decrireLaCible(step: PlanStep): string {
  return step.selector.kind === 'nodeId' ? `block ${step.selector.nodeId}` : 'its target block';
}

/** Longueur maximale de ce que l'éditeur dit d'un refus ou d'une exception, recopié dans un reçu. */
const LONGUEUR_MAX_DE_LA_RAISON_DE_LEDITEUR = 300;

function borner(texte: string, longueurMax: number): string {
  return texte.length > longueurMax ? `${texte.slice(0, longueurMax)}…` : texte;
}

/**
 * Les deux codes par lesquels l'aperçu dit « ce texte n'est pas là une fois, et une seule ». Eux seuls se corrigent en
 * changeant le texte cherché, sur le même document.
 */
const CODES_DE_RECHERCHE: ReadonlySet<string> = new Set(['MATCH_NOT_FOUND', 'AMBIGUOUS_MATCH']);

/** Vrai quand le premier refus de l'aperçu se corrige dans le plan, sans relire : une étape nommée, refusée pour son texte. */
function seCorrigeSansRelire(preview: ProviderPreview): boolean {
  const failure = preview.failures?.[0];
  return failure !== undefined && CODES_DE_RECHERCHE.has(failure.code);
}

/**
 * Le refus d'aperçu qui ne tient PAS au texte cherché : le code et la raison de l'éditeur, recopiés.
 *
 * WHY : tout code autre que les deux codes de recherche rendait « found an unreported number of occurrence(s) …
 * lengthen its text », avec un `recovery` qui disait de renvoyer le plan sans relire. Pour un refus de capacité, de
 * révision ou d'adresse, la cause annoncée était fausse et le conseil ne pouvait qu'échouer (conversation de
 * production du 2026-10-13).
 */
function messageDuRefusHorsRecherche(step: PlanStep | undefined, failure: NonNullable<ProviderPreview['failures']>[number]): string {
  const etape = step === undefined ? `step "${failure.stepId}" (${failure.code})` : `step "${step.id}" (${step.op}, ${failure.code}) on ${decrireLaCible(step)}`;
  const raison = failure.message === undefined || failure.message.trim().length === 0
    ? 'The editor gave no reason with that code.'
    : `The editor's reason: ${JSON.stringify(borner(failure.message, LONGUEUR_MAX_DE_LA_RAISON_DE_LEDITEUR))}.`;
  const relire = step?.selector.kind === 'nodeId'
    ? `canvas_inspect with selector:{kind:"nodeId",nodeId:"${step.selector.nodeId}"} and includeDomains:["blocks","trackedChanges"]`
    : 'canvas_inspect with includeDomains:["blocks","trackedChanges"]';
  return `apply_edit_plan was refused by native preview at ${etape} before any write. ${raison} Nothing was written. `
    + 'This refusal is not about the text the step looks for: recopying or lengthening that text will not lift it, and the same plan resent unchanged will be refused again. '
    + `Next step: ${relire} to read that block as it is now (its text, and any tracked change pending on it), then rebuild the step from what it returns. `
    + `If ${failure.code} comes back on the rebuilt plan, stop resending it and tell the lawyer which change could not be applied.`;
}

/**
 * Dit à l'agent QUELLE étape a fait refuser le plan, ce qu'elle cherchait et
 * combien de fois le texte a été trouvé.
 *
 * WHY : le refus ne rendait qu'un id d'étape et un code fournisseur, suivis de
 * « run canvas_inspect ». Or la prévisualisation n'écrit rien et le plan est
 * évalué sur la révision inspectée : relire rend le même document, et l'agent
 * rejouait le même plan après chaque relecture (audit 2026-10-01). La cause
 * presque unique est un `find` trouvé 0 ou plusieurs fois dans son bloc — une
 * information que la prévisualisation porte déjà (`matchCount`).
 */
function previewFailureMessage(input: ApplyEditPlanInput, preview: ProviderPreview): string {
  const failure = preview.failures?.[0];
  if (failure === undefined) {
    return 'apply_edit_plan was refused by native preview before any write, but the provider returned no named failed step. Nothing was modified; run canvas_inspect and rebuild the plan from the current document.';
  }
  const step = input.steps.find((candidate) => candidate.id === failure.stepId);
  if (!CODES_DE_RECHERCHE.has(failure.code)) return messageDuRefusHorsRecherche(step, failure);
  const suite = 'Nothing was written and your scope is intact: correct that step and resend the plan, without re-reading.';
  if (step === undefined) {
    return `apply_edit_plan was refused by native preview at step "${failure.stepId}" (${failure.code}) before any write. ${suite}`;
  }
  const cherche = texteCherche(step);
  if (cherche === undefined) {
    return `apply_edit_plan was refused by native preview at step "${step.id}" (${step.op}, ${failure.code}) on ${decrireLaCible(step)} before any write. ${suite}`;
  }
  const extrait = borner(cherche, LONGUEUR_MAX_DU_TEXTE_CHERCHE);
  // Une prévisualisation en échec ne rend pas toujours `matchCount` ; les deux
  // codes natifs de recherche disent alors le compte à eux seuls.
  const compte = preview.steps.find((candidate) => candidate.stepId === step.id)?.matchCount;
  const absent = compte === 0 || (compte === undefined && failure.code === 'MATCH_NOT_FOUND');
  const trouve = compte !== undefined ? String(compte) : failure.code === 'MATCH_NOT_FOUND' ? '0' : 'several';
  const attendu = step.op === 'assert_text' ? `exactly ${step.expectedCount}` : 'exactly 1';
  const conseil = step.op === 'assert_text'
    ? 'fix its text or expectedCount'
    : absent
      ? 'copy the text exactly as the block reads (case-sensitive, plain text without ** or *), or drop the step if the edit is already done'
      : 'lengthen its text with the words around it until it occurs once in that block (several edits of the same word in one block: one step each, each with its own context)';
  return `apply_edit_plan was refused by native preview at step "${step.id}" (${step.op}, ${failure.code}) before any write: `
    + `it looked for ${JSON.stringify(extrait)} in ${decrireLaCible(step)} and found ${trouve} occurrence(s), expected ${attendu}. `
    + `${suite} To correct it: ${conseil}.`;
}

/**
 * Ce que le reçu dit quand `mutations.apply` a LEVÉ, hors du refus de champ Word traité par l'appelant.
 *
 * WHY relire la révision : le reçu disait toujours « soumis, sans accusé, peut-être écrit », alors qu'un plan
 * atomique que l'éditeur refuse en levant laisse la révision où elle était (conversation de production du
 * 2026-10-13 : une écriture annoncée incertaine sur un plan dont rien n'était parti).
 * Révision inchangée : rien n'est écrit, le refus se dit avec l'erreur de l'éditeur. Révision avancée ou illisible :
 * rien ne le prouve, le reçu reste inconclusif et dit lequel des deux.
 */
async function recuDeLExceptionALApplication(
  ctx: CanvasActionContext,
  input: ApplyEditPlanInput,
  erreur: unknown,
  preSummary: ReturnType<typeof toCanvasSnapshotSummary>,
  resolvedTargets: ResolvedActionTarget[],
): Promise<CanvasActionReceipt> {
  const raison = JSON.stringify(borner(erreur instanceof Error ? erreur.message : String(erreur), LONGUEUR_MAX_DE_LA_RAISON_DE_LEDITEUR));
  // Sans repli : une révision illisible ne doit pas se lire comme « inchangée ».
  const apres = await lireLaRevisionDuDocument(ctx.doc).catch(() => undefined);
  const rienNEstEcrit = apres !== undefined && apres === ctx.expectedRevision;
  if (ctx.canvasRequestId !== undefined) {
    // Le message de l'erreur ne part pas : un moteur d'édition peut y citer le texte de l'acte.
    captureCanvasAgentTransportDiagnostic({
      stage: 'edit_plan_apply_threw',
      requestId: ctx.canvasRequestId,
      tool: 'canvas_perform_action',
      action: input.action,
      outcome: rienNEstEcrit ? 'refused' : 'unconfirmed',
      errorName: erreur instanceof Error ? erreur.name : typeof erreur,
    });
  }
  if (rienNEstEcrit) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: preSummary,
      code: 'EDIT_PLAN_APPLY_REFUSED',
      message: `apply_edit_plan was refused by the editor while applying the plan: it threw ${raison}. The document revision is still ${apres}. `
        + 'Nothing was written; the same plan will be refused again. '
        + 'Next step: read the editor\'s reason above and rebuild the plan so that it avoids it. When the reason names no step and the steps do not depend on one another, resend each as its own one-step apply_edit_plan to find the one the editor refuses, and tell the lawyer which change could not be applied.',
      recovery: { action: 'reinspect', reason: 'The editor threw on apply and the document revision did not move: nothing was written, and the same plan resent unchanged will be refused again.' },
    });
  }
  const constat = apres === undefined
    ? 'the document revision could not be read afterwards'
    : `the document revision moved from ${ctx.expectedRevision} to ${apres}`;
  return inconclusiveAfterMutation({
    intent: input.action,
    snapshot: preSummary,
    resolvedTargets,
    code: 'EDIT_PLAN_APPLY_UNCONFIRMED',
    message: `apply_edit_plan was submitted and the editor threw ${raison} without returning a receipt, and ${constat}: the plan may or may not have been written. Do not retry it. Run canvas_inspect and decide from the live document.`,
    recoveryReason: `The editor threw on apply and ${constat}: nothing proves whether the plan was written.`,
  });
}

/**
 * Le paragraphe de cellule qu'une étape vise par un `nodeId` que la liste des blocs ne porte pas, ou son refus rédigé ;
 * `undefined` : ce n'est pas un paragraphe de cellule que la dernière lecture a publié, le refus d'origine tient.
 * Règle et mesures : `resoudreUnParagrapheDeCellule` (`selectorResolver.ts`).
 */
async function celluleVisee(
  ctx: CanvasActionContext,
  step: PlanStep,
): Promise<Extract<ParagrapheDeCelluleResolu, { ok: true }> | { refus: string } | undefined> {
  if (step.selector.kind !== 'nodeId') return undefined;
  const { nodeId } = step.selector;
  const intention = `apply_edit_plan step "${step.id}" (${step.op})`;
  const cherche = texteCherche(step);
  if (cherche === undefined) {
    // `text_insert` adresse un bloc entier : non mesuré dans une cellule, et aucun texte cherché ne dit son tableau.
    if (ctx.inspectedPublicNodeIds?.has(nodeId) !== true) return undefined;
    const enCellule = await estUnParagrapheDeCelluleAuFichier(ctx.doc, nodeId);
    return { refus: messageParagrapheDeCelluleNonResolu(intention, nodeId, enCellule ? 'bloc_entier' : 'hors_cellule') };
  }
  const resolue = await resoudreUnParagrapheDeCellule(ctx.doc, nodeId, cherche, ctx.inspectedPublicNodeIds);
  if (resolue.ok) return resolue;
  if (resolue.raison === 'hors_lecture') return undefined;
  return { refus: messageParagrapheDeCelluleNonResolu(intention, nodeId, resolue.raison === 'hors_cellule' ? 'hors_cellule' : { cherche, raison: resolue.raison }) };
}

/**
 * Execute a heterogeneous edit plan as one native tracked mutation.
 *
 * A preview failure or a revision change between preview and apply is an
 * `aborted` receipt because no mutation was attempted. Once `apply` has been
 * called, every abnormal provider shape is non-retriable: the effect may have
 * settled even when it cannot be attributed safely.
 */
export async function executeApplyEditPlan(
  ctx: CanvasActionContext,
  input: ApplyEditPlanInput,
): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  const preSummary = toCanvasSnapshotSummary(pre);
  if (ctx.expectedRevision === undefined) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: preSummary,
      code: 'EXPECTED_REVISION_REQUIRED',
      message: 'apply_edit_plan was refused before preview because the action envelope carries no expected document revision. Nothing was modified; run canvas_inspect and build a new plan from its revision.',
    });
  }
  if (typeof ctx.doc.mutations?.preview !== 'function' || typeof ctx.doc.mutations.apply !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: preSummary,
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'apply_edit_plan requires both editor.doc.mutations.preview and editor.doc.mutations.apply. Nothing was modified; use an available Canvas action instead.',
    });
  }

  const resolvedTargets: ResolvedActionTarget[] = [];
  // Le bloc visé par chaque étape, avec son type : la relecture de forme ne couvre pas un passage en cellule de tableau.
  const blocsVises: Array<{ nodeId: string; nodeType: string }> = [];
  const providerSteps: Record<string, unknown>[] = [];
  for (const step of input.steps) {
    const resolved = await resoudreLaCible(ctx, step.selector, input.action);
    if (!resolved.ok) {
      const cellule = resolved.code === 'SELECTOR_NOT_FOUND' ? await celluleVisee(ctx, step) : undefined;
      if (cellule !== undefined && 'tableau' in cellule) {
        // L'étape cherche DANS le paragraphe de cellule, où l'éditeur borne sa recherche ; son tableau est le bloc relu.
        resolvedTargets.push(targetSummary(cellule.tableau));
        blocsVises.push(cellule.adresse);
        providerSteps.push(compilerEtape(step, cellule.adresse));
        continue;
      }
      return abortedBeforeMutation({
        intent: input.action,
        snapshot: preSummary,
        code: resolved.code,
        stepId: step.id,
        message: cellule === undefined
          ? `apply_edit_plan was refused before preview at step "${step.id}" (${step.op}). ${resolved.message}`
          : cellule.refus,
      });
    }
    resolvedTargets.push(targetSummary(resolved.target, resolved.candidateCount));
    blocsVises.push({ nodeId: resolved.target.nodeId, nodeType: resolved.target.nodeType });
    providerSteps.push(compilerEtape(step, resolved.target));
  }

  if (pre.revision !== ctx.expectedRevision) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: preSummary,
      code: 'STALE_CANVAS_REVISION',
      message: `apply_edit_plan was refused before preview because the live document revision (${pre.revision}) no longer equals the inspected revision (${ctx.expectedRevision}). Nothing was modified; run canvas_inspect and rebuild the full plan.`,
    });
  }

  // The atomic plan is still one provider call, but its review mode belongs to
  // the document. Forcing `tracked` here while the lawyer has disabled tracking
  // opens a suggestion window they did not choose and makes a direct edit look
  // like `TRACKING_NOT_APPLIED` in the receipt.
  const mode = modeDeRevision(ctx);
  const suiviActif = mode === 'tracked';
  const plan = { atomic: true as const, changeMode: mode, expectedRevision: ctx.expectedRevision, steps: providerSteps };
  let preview: ProviderPreview | null;
  // Ce que l'aperçu résout, étape par étape : pour un tableau visé, le paragraphe de cellule où l'éditeur écrit (`formeDuPlan.ts`).
  let resolutions: Map<string, ResolutionDEtape[]> = new Map();
  try {
    const apercu = await ctx.doc.mutations.preview(plan);
    preview = lirePreview(apercu);
    resolutions = resolutionsParEtape(apercu);
  } catch {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: preSummary,
      code: 'EDIT_PLAN_PREVIEW_UNAVAILABLE',
      message: 'apply_edit_plan could not complete native preview before any write. Nothing was modified; run canvas_inspect after the editor is ready, then rebuild the plan.',
    });
  }
  if (preview === null || preview.evaluatedRevision !== ctx.expectedRevision) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: preSummary,
      code: 'EDIT_PLAN_PREVIEW_UNVERIFIABLE',
      message: 'apply_edit_plan was refused before any write because native preview did not return the expected document revision. Nothing was modified; run canvas_inspect and rebuild the plan.',
    });
  }
  if (!preview.valid) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: preSummary,
      code: 'EDIT_PLAN_PREVIEW_FAILED',
      stepId: preview.failures?.[0]?.stepId,
      message: previewFailureMessage(input, preview),
      // `retry` seulement pour une étape refusée sur son texte : relire rendrait le même document, et le courtier garde
      // la portée de ce reçu `aborted`. Tout autre refus garde `reinspect` : le même plan serait refusé à nouveau.
      ...(seCorrigeSansRelire(preview)
        ? { recovery: { action: 'retry' as const, reason: 'Nothing was written and the inspected scope is intact: correct the named step and resend the plan, without re-reading.' } }
        : {}),
    });
  }
  if (!previewCoversExactly(input, preview)) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: preSummary,
      code: 'EDIT_PLAN_PREVIEW_UNVERIFIABLE',
      message: 'apply_edit_plan was refused before any write because native preview did not return the exact ordered plan. Nothing was modified; run canvas_inspect and rebuild the plan.',
    });
  }

  // This second fence is intentionally after preview: a lawyer may edit while
  // preview resolves targets. `apply` must never be the first operation that
  // discovers the stale revision, because it is the only operation that writes.
  ctx.verifierEpoque('apply_edit_plan:after-preview');
  const revisionBeforeApply = await revisionCourante(ctx, pre.revision);
  if (revisionBeforeApply !== ctx.expectedRevision) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: preSummary,
      code: 'STALE_CANVAS_REVISION',
      message: `apply_edit_plan was refused after preview because the document revision changed from ${ctx.expectedRevision} to ${revisionBeforeApply}. No plan step was applied; run canvas_inspect and rebuild the complete plan.`,
    });
  }

  let providerResult: unknown;
  try {
    providerResult = await muterSousFences(ctx, 'apply_edit_plan:apply', {
      changeMode: mode,
      expectedRevision: ctx.expectedRevision,
    }, () => ctx.doc.mutations?.apply?.(plan));
  } catch (erreur) {
    // Le document a été remplacé sous le plan : ce n'est pas l'éditeur qui refuse, et l'appelant sait le dire.
    if (estArretDeLot(erreur)) throw erreur;
    // Un texte cherché qui traverse un champ Word vide fait JETER l'application (l'aperçu ne le voit pas). La
    // révision inchangée prouve qu'aucune étape n'est écrite : le refus se dit, avec le verbe qui passe.
    if (String(erreur instanceof Error ? erreur.message : erreur).includes(REFUS_TEXTE_DANS_UN_CHAMP)
      && (await revisionCourante(ctx, pre.revision)) === ctx.expectedRevision) {
      return abortedBeforeMutation({
        intent: input.action,
        snapshot: preSummary,
        code: 'EDIT_PLAN_TEXT_SPANS_FIELD',
        message: `apply_edit_plan wrote nothing: the editor refused the plan (${REFUS_TEXTE_DANS_UN_CHAMP}) because the text searched by one of its text steps spans a Word field. `
          + `The document revision is still ${ctx.expectedRevision} and the document is NOT locked: do not tell the lawyer to finish it in Word. `
          + 'Next step: apply each text edit with replace_text, or rewrite the whole block with rewrite_block; both remove the empty leftover fields of the block before writing. Then resend the remaining steps as a new plan.',
        recovery: { action: 'retry', reason: 'Nothing was written. replace_text and rewrite_block remove the empty fields that block this plan; the same plan resent as is will be refused again.' },
      });
    }
    return recuDeLExceptionALApplication(ctx, input, erreur, preSummary, resolvedTargets);
  }

  let post;
  try {
    post = await snapshot(ctx);
  } catch {
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSummary,
      resolvedTargets,
      code: 'EDIT_PLAN_POST_READ_UNAVAILABLE',
      message: 'apply_edit_plan was submitted, but the post-application document state could not be read. The plan may or may not have settled; do not retry it. Run canvas_inspect and decide from the live document.',
      recoveryReason: 'The plan was submitted and the document could not be read afterwards: nothing proves whether it was written.',
    });
  }
  const provider = lireRecuDePlan(providerResult);
  if (provider === null) {
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSummary,
      postSnapshot: toCanvasSnapshotSummary(post),
      resolvedTargets,
      code: 'EDIT_PLAN_RECEIPT_UNVERIFIABLE',
      message: 'apply_edit_plan returned a receipt that cannot be matched to the requested atomic plan. The document may have changed; do not retry it. Run canvas_inspect and decide from the live document.',
      recoveryReason: 'The editor answered the plan with a receipt that does not have the expected shape: nothing proves which steps were written.',
    });
  }

  const editPlan = { revision: provider.revision, steps: proofFromReceipt(input, provider) };
  const revisionsConsistent = provider.revision.before === pre.revision && provider.revision.after === post.revision;
  const checks: VerificationCheck[] = [
    {
      name: 'native_plan_revisions',
      passed: revisionsConsistent,
      message: 'The native plan receipt revisions must exactly match the document reads before and after apply.',
    },
    ...editPlan.steps.map((step) => ({
      name: `native_plan_step:${step.id}`,
      passed: step.verified,
      message: step.message ?? 'The native plan receipt proved this requested step.',
    })),
  ];
  if (!revisionsConsistent || editPlan.steps.some((step) => !step.verified)) {
    const firstUnverifiedStep = editPlan.steps.find((step) => !step.verified);
    return {
      ...inconclusiveAfterMutation({
        intent: input.action,
        snapshot: preSummary,
        postSnapshot: toCanvasSnapshotSummary(post),
        resolvedTargets,
        code: 'EDIT_PLAN_RECEIPT_UNVERIFIABLE',
        stepId: firstUnverifiedStep?.id,
        message: 'apply_edit_plan returned, but at least one native step or revision cannot be proven against the live document. Do not retry it; run canvas_inspect and decide from the current document.',
        recoveryReason: revisionsConsistent
          ? 'The editor\'s receipt does not prove every requested step (see editPlan.steps[].verified): the plan may be partly written.'
          : `The revisions of the editor's receipt (${provider.revision.before} to ${provider.revision.after}) differ from the document reads before and after apply (${pre.revision} to ${post.revision}): the document moved for another reason, the plan's effect cannot be attributed.`,
      }),
      editPlan,
    };
  }

  // La forme posée par les étapes `format_apply` est relue au fichier exporté, clôturé sur la révision que le plan a
  // laissée (prouvée égale à celle de l'instantané d'après, ci-dessus). Un plan sans étape de forme n'exporte rien.
  const etapesDeForme = etapesDeFormeDuPlan(input.steps, blocsVises, resolutions);
  const forme = etapesDeForme.length === 0
    ? undefined
    : jugerLaFormeDuPlan(corpsRelu(await relireLActeApresLEcriture(ctx, post.revision === REVISION_ABSENTE ? undefined : post.revision)), etapesDeForme);

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const revisionsApres = idsDeRevision(post.trackedChanges);
  const absorbantes = suiviActif ? revisionsAbsorbantesDuPlan(input, provider, revisionsAvant, revisionsApres) : [];
  const effect = {
    kind: 'apply_edit_plan',
    reviewability: suiviActif ? 'tracked' as const : 'direct_untracked' as const,
    description: absorbantes.length === 0
      ? 'Applied one native atomic plan of linked document retouches in the document review mode.'
      : `Applied one native atomic plan of linked document retouches. No new tracked change was created: every edit went into tracked change(s) already pending on its block (${absorbantes.slice(0, 8).join(', ')}), where the lawyer still reviews it.`,
    nodeIds: [...new Set(resolvedTargets.map((target) => target.nodeId))],
  };
  // Native plan receipts prove the provider-level effects. Re-reading tracked
  // identities establishes whether the lawyer can actually review the plan;
  // it does not make any statement about save/autosave or DOCX integrity.
  const receipt = buildVerifiedReceipt({
    intent: input.action,
    preSnapshot: preSummary,
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets,
    provider: providerResult,
    revisionsAvant,
    revisionsApres,
    revisionsAbsorbantes: absorbantes,
    mouvementAttendu: suiviActif ? 'apparition' : 'aucun',
    regimeSuivi: ctx.regimeSuivi,
    verification: checks,
    effects: [effect],
  });
  if (suiviActif && receipt.status === 'failed' && revisionsApres.every((id) => revisionsAvant.includes(id))) {
    return recuAvecLaFormeDuPlan({
      ...partialAfterMutation({
        intent: input.action,
        preSnapshot: preSummary,
        postSnapshot: toCanvasSnapshotSummary(post),
        resolvedTargets,
        code: 'TRACKING_NOT_APPLIED',
        message: 'apply_edit_plan changed every requested target, but the post-read document contains no new tracked-change identity. Do not retry the plan: its edits are already present without a reviewable tracked trace.',
        verification: [...checks, {
          name: 'new_revision_identities',
          passed: false,
          message: 'A tracked plan must create at least one new tracked-change identity.',
        }],
        effects: [effect],
        recoveryReason: 'The native plan effects are present but were not recorded as tracked changes; replaying would duplicate them.',
      }),
      editPlan,
    }, forme);
  }
  ctx.verifierEpoque('apply_edit_plan:receipt');
  return recuAvecLaFormeDuPlan({ ...receipt, editPlan }, forme);
}
